import {
  all, call, put, takeLatest,
} from 'redux-saga/effects';
import {
  type FetchableResource, FetchableResourceStatus,
} from '~/_shared/types/fetchableResource/fetchableResource';
import { select } from '~/_shared/utils/saga/effects';
import {
  convertPlacesSearchToBoundingBox, placesSearchToPlacesSearchLocation,
} from '~/places/places.helpers';
import {
  getPlacesByTextSearch, type GetPlacesByTextSearchResponse, type PlacesSearchProvider,
} from '~/places/places.repository';
import { mapSettingsPlacesAddSearch } from '~/store/mapSettings/places/mapSettingsPlaces.actionCreators';
import { mapSettingsPlacesSearchesSelector } from '~/store/mapSettings/places/mapSettingsPlaces.selectors';
import type {
  PlacesSearch, PlacesSearchId,
} from '~/store/mapSettings/places/mapSettingsPlaces.state';
import { clientIdSelector } from '~/store/selectors/useClientIdSelector';
import { mapIdSelector } from '~/store/selectors/useMapIdSelector';
import { isMapPresentationalSelector } from '~/store/selectors/useMapInfoSelectors';
import { userSetPlacesApiUsage } from '~/store/userData/userData.actionCreators';
import { MAP_SETTINGS_FETCH_DATA_SUCCESS } from '../../mapSettings/data/mapSettingsData.actionTypes';
import { mapComponentSetZoomToBounds } from '../mapComponent/mapComponent.actionCreators';
import {
  placesSearchesGetResultsSuccess,
  placesSearchesRequestNextPage, placesSearchesSetLoading,
} from './places.actionCreators';
import { placesSearchResultsSelector } from './places.selectors';
import type { PlacesSearchResult } from './places.state';

export function* placesSagas() {
  yield takeLatest(MAP_SETTINGS_FETCH_DATA_SUCCESS, fetchPlacesSearchesData);
  yield takeLatest(mapSettingsPlacesAddSearch, fetchPlacesSearchesData);
  yield takeLatest(mapSettingsPlacesAddSearch, zoomToResultsIfNeeded);
  yield takeLatest(placesSearchesRequestNextPage, fetchPlacesSearchesData);
}

function* fetchPlacesSearchesData() {
  const clientId: number | null = yield select(clientIdSelector);
  const searches: ReadonlyArray<PlacesSearch> = yield select(mapSettingsPlacesSearchesSelector);
  const searchesResults: ReadonlyMap<PlacesSearchId, FetchableResource<PlacesSearchResult>> = yield select(placesSearchResultsSelector);

  const searchesToFetch: PlacesSearch[] = [];
  const searchIdsToGetNextPage = new Set<PlacesSearchId>();

  searches.forEach((search) => {
    const searchResult = searchesResults.get(search.id);
    if (!searchResult) {
      searchesToFetch.push(search);
    }
    else if (searchResult.status === FetchableResourceStatus.NextPageRequested) {
      searchesToFetch.push(search);
      searchIdsToGetNextPage.add(search.id);
    }
  });

  if (!searchesToFetch.length || !clientId) {
    return;
  }

  yield put(placesSearchesSetLoading(new Set(searchesToFetch.map((search) => search.id))));

  // each provider has its own endpoint, so the searches are fetched in one request per provider
  const searchesByProvider = new Map<PlacesSearchProvider, PlacesSearch[]>();
  searchesToFetch.forEach((search) => {
    const providerSearches = searchesByProvider.get(search.provider);
    if (providerSearches) {
      providerSearches.push(search);
    }
    else {
      searchesByProvider.set(search.provider, [search]);
    }
  });

  const mapId: ReturnType<typeof mapIdSelector> = yield select(mapIdSelector);
  const isMapPresentational: ReturnType<typeof isMapPresentationalSelector> = yield select(isMapPresentationalSelector);

  const providerGroups = [...searchesByProvider.entries()];
  const responses: ReadonlyArray<GetPlacesByTextSearchResponse | null> = yield all(
    providerGroups.map(([provider, providerSearches]) => call(
      fetchProviderSearchesData,
      {
        clientId,
        mapId: isMapPresentational && mapId ? mapId : undefined,
        provider,
        searches: providerSearches,
        searchIdsToGetNextPage,
      },
    ))
  );

  const results = new Map<PlacesSearchId, FetchableResource<PlacesSearchResult>>();
  let placesApiUsage = 0;

  providerGroups.forEach(([, providerSearches], groupIndex) => {
    const response = responses[groupIndex];

    if (!response) {
      return;
    }

    placesApiUsage = response.data.reduce((acc, item) => Math.max(acc, item.google_places_requests_usage), placesApiUsage);

    providerSearches.forEach((searchToFetch, index) => {
      const responseForSearch = response.data?.[index];

      results.set(
        searchToFetch.id,
        responseForSearch
          ? {
            status: FetchableResourceStatus.Loaded,
            value: {
              id: searchToFetch.id,
              items: responseForSearch.items || [],
              hasMore: !!responseForSearch.hasMore,
              error: responseForSearch.code,
            },
          }
          : { status: FetchableResourceStatus.NotFound },
      );
    });
  });

  if (!results.size) {
    return;
  }

  yield put(userSetPlacesApiUsage(placesApiUsage));
  yield put(placesSearchesGetResultsSuccess(results));
}

function* fetchProviderSearchesData({ clientId, mapId, provider, searches, searchIdsToGetNextPage }: {
  clientId: number;
  mapId: number | undefined;
  provider: PlacesSearchProvider;
  searches: ReadonlyArray<PlacesSearch>;
  searchIdsToGetNextPage: ReadonlySet<PlacesSearchId>;
}) {
  const requestsList = searches.map((search) => ({
    text: search.text,
    location: placesSearchToPlacesSearchLocation(search),
    preference: search.preference,
    more: searchIdsToGetNextPage.has(search.id) ? true : undefined,
  }));

  try {
    const response: GetPlacesByTextSearchResponse = yield call(
      getPlacesByTextSearch(provider),
      clientId,
      { params: requestsList },
      mapId,
    );

    return response;
  }
  catch (e) {
    console.error(e);
    return null;
  }
}

function* zoomToResultsIfNeeded(action: ReturnType<typeof mapSettingsPlacesAddSearch>) {
  const { newSearch, zoomTo } = action.payload;

  if (!zoomTo) {
    return;
  }

  const bounds = convertPlacesSearchToBoundingBox(newSearch);
  yield put(mapComponentSetZoomToBounds(bounds));
}
